import type { BitPalmAgentOptions, BitPalmAgentInstance } from "./types";

const DEFAULT_BASE_URL = "https://agents.bitpalm.ae";
const STORAGE_PREFIX = "_bp_";

function detectLocale(): string {
  if (typeof document !== "undefined") {
    const lang = document.documentElement.lang;
    if (lang) return lang.slice(0, 2).toLowerCase();
  }
  return "en";
}

export function createWidget(options: BitPalmAgentOptions): BitPalmAgentInstance {
  const {
    slug,
    locale = detectLocale(),
    baseUrl = DEFAULT_BASE_URL,
    zIndex = 9999,
  } = options;

  const d = document;

  // --- Iframe ---
  const iframe = d.createElement("iframe");
  const widgetUrl = `${baseUrl}/t/${slug}/widget`;
  iframe.src = `${widgetUrl}${widgetUrl.includes("?") ? "&" : "?"}locale=${locale}`;
  iframe.allow = "clipboard-write";
  iframe.setAttribute("allowtransparency", "true");
  iframe.style.cssText = `position:fixed;bottom:0;right:0;width:1px;height:1px;border:none;z-index:${zIndex};background:transparent;color-scheme:normal;opacity:0;pointer-events:none;`;
  d.body.appendChild(iframe);

  const origin = new URL(iframe.src).origin;

  // --- Native launcher button ---
  const btn = d.createElement("div");
  btn.setAttribute("role", "button");
  btn.setAttribute("aria-label", "Chat");
  btn.style.cssText = `position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;display:none;align-items:center;justify-content:center;cursor:pointer;z-index:${zIndex - 1};background:#00ff41;box-shadow:0 0 15px rgba(0,255,65,0.31);transition:transform .2s;`;
  btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>';
  btn.onmouseenter = () => { btn.style.transform = "scale(1.1)"; };
  btn.onmouseleave = () => { btn.style.transform = "scale(1)"; };
  btn.onclick = () => openWidget();
  d.body.appendChild(btn);

  // --- Fade animation for trigger bubbles ---
  if (!d.getElementById("_bp_fade")) {
    const s = d.createElement("style");
    s.id = "_bp_fade";
    s.textContent = "@keyframes _bp_fade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}";
    d.head.appendChild(s);
  }

  // --- Trigger bubble ---
  let bubble: HTMLDivElement | null = null;
  const removeBubble = () => {
    if (bubble) { bubble.remove(); bubble = null; }
  };

  // --- postMessage handler ---
  const handleMessage = (e: MessageEvent) => {
    if (e.origin !== origin) return;

    // Widget config (colors, icon)
    if (e.data?.type === "bitpalm-widget-config") {
      if (e.data.primaryColor) {
        btn.style.background = e.data.primaryColor;
        btn.style.boxShadow = e.data.chatShadow === "none" ? "none" : `0 0 15px ${e.data.primaryColor}50`;
      }
      const ic = e.data.chatButtonIconColor || "black";
      if (e.data.chatButtonIconSvg) {
        btn.innerHTML = "";
        const sp = d.createElement("span");
        sp.style.cssText = `display:flex;align-items:center;justify-content:center;width:24px;height:24px;color:${ic}`;
        sp.innerHTML = e.data.chatButtonIconSvg;
        const svg = sp.querySelector("svg");
        if (svg) {
          svg.setAttribute("width", "24");
          svg.setAttribute("height", "24");
          svg.style.display = "block";
        }
        btn.appendChild(sp);
      } else {
        const sv = btn.querySelector("svg");
        if (sv) (sv as SVGElement).style.stroke = ic;
      }
      btn.style.display = "flex";
    }

    // Resize — use full viewport on mobile
    if (e.data?.type === "bitpalm-widget-resize") {
      const isOpen = e.data.width > 0;
      const mobile = window.innerWidth <= 640;
      if (isOpen) {
        iframe.style.width = mobile ? "100vw" : `${e.data.width}px`;
        iframe.style.height = mobile ? "100dvh" : `${e.data.height}px`;
        if (mobile) { iframe.style.left = "0"; iframe.style.top = "0"; }
        iframe.style.opacity = "1";
        iframe.style.pointerEvents = "auto";
      } else {
        iframe.style.width = "1px";
        iframe.style.height = "1px";
        iframe.style.left = "";
        iframe.style.top = "";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
      }
      btn.style.display = isOpen ? "none" : "flex";
    }

    // Trigger bubble show
    if (e.data?.type === "bitpalm-trigger-show") {
      removeBubble();
      bubble = d.createElement("div");
      bubble.style.cssText = `position:fixed;bottom:90px;right:24px;max-width:240px;background:#fff;border-radius:16px;padding:12px 32px 12px 12px;box-shadow:0 2px 20px rgba(0,0,0,0.08);cursor:pointer;z-index:${zIndex - 1};font-family:system-ui,sans-serif;animation:_bp_fade .3s;`;
      const msg = (e.data.message || "").replace(/</g, "&lt;");
      bubble.innerHTML = `<p style="font-size:14px;color:#1f2937;margin:0;">${msg}</p><div style="position:absolute;bottom:-8px;right:20px;width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid #fff;"></div>`;
      const close = d.createElement("button");
      close.style.cssText = "position:absolute;top:-8px;right:-8px;width:28px;height:28px;border-radius:50%;background:#fff;border:none;box-shadow:0 1px 4px rgba(0,0,0,0.1);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;color:#9ca3af;";
      close.innerHTML = "&#10005;";
      close.onclick = (ev) => { ev.stopPropagation(); removeBubble(); iframe.contentWindow?.postMessage({ type: "bitpalm-trigger-dismiss" }, "*"); };
      bubble.onclick = () => { removeBubble(); btn.style.display = "none"; iframe.contentWindow?.postMessage({ type: "bitpalm-trigger-engaged" }, "*"); };
      bubble.appendChild(close);
      d.body.appendChild(bubble);
    }

    // Trigger bubble hide
    if (e.data?.type === "bitpalm-trigger-hide") { removeBubble(); }
  };
  window.addEventListener("message", handleMessage);

  // --- Visitor Intelligence: page counter + history ---
  let pageCount = parseInt(sessionStorage.getItem(`${STORAGE_PREFIX}pages`) || "0") + 1;
  sessionStorage.setItem(`${STORAGE_PREFIX}pages`, String(pageCount));

  let hist: string[] = JSON.parse(sessionStorage.getItem(`${STORAGE_PREFIX}hist`) || "[]");
  hist.push(location.href);
  if (hist.length > 20) hist = hist.slice(-20);
  sessionStorage.setItem(`${STORAGE_PREFIX}hist`, JSON.stringify(hist));

  const loadedAt = Date.now();
  let siteStart = parseInt(sessionStorage.getItem(`${STORAGE_PREFIX}site_start`) || "0");
  if (!siteStart) {
    siteStart = loadedAt;
    sessionStorage.setItem(`${STORAGE_PREFIX}site_start`, String(siteStart));
  }

  // --- Send parent context on iframe load ---
  iframe.addEventListener("load", () => {
    const returning = !!localStorage.getItem(`${STORAGE_PREFIX}visited`);
    iframe.contentWindow?.postMessage({
      type: "bitpalm-parent-context",
      url: location.href,
      title: d.title,
      returning,
      pageCount,
      pageHistory: hist,
      loadedAt,
      siteStart,
      locale,
    }, "*");
    if (!returning) localStorage.setItem(`${STORAGE_PREFIX}visited`, "1");
  });

  // --- SPA navigation detection ---
  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  const onNav = () => {
    pageCount++;
    sessionStorage.setItem(`${STORAGE_PREFIX}pages`, String(pageCount));
    const h: string[] = JSON.parse(sessionStorage.getItem(`${STORAGE_PREFIX}hist`) || "[]");
    h.push(location.href);
    if (h.length > 20) h.splice(0, h.length - 20);
    sessionStorage.setItem(`${STORAGE_PREFIX}hist`, JSON.stringify(h));
    iframe.contentWindow?.postMessage({
      type: "bitpalm-page-change",
      url: location.href,
      title: d.title,
      pageCount,
      pageHistory: h,
      loadedAt: Date.now(),
    }, "*");
  };

  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    origPush(...args);
    onNav();
  };
  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    origReplace(...args);
    onNav();
  };
  window.addEventListener("popstate", onNav);

  // --- Scroll depth tracking ---
  let lastScrollDepth = -1;
  let scrollTimeout: ReturnType<typeof setTimeout>;
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const total = Math.max(d.documentElement.scrollHeight, d.body.scrollHeight) - window.innerHeight;
      const pct = total > 0 ? Math.round(window.scrollY / total * 100) : 100;
      if (pct !== lastScrollDepth) {
        lastScrollDepth = pct;
        iframe.contentWindow?.postMessage({ type: "bitpalm-scroll-depth", percent: pct }, "*");
      }
    }, 500);
  };
  window.addEventListener("scroll", handleScroll, { passive: true });

  // --- Exit intent ---
  const handleMouseOut = (e: MouseEvent) => {
    if (e.clientY <= 0) {
      iframe.contentWindow?.postMessage({ type: "bitpalm-exit-intent" }, "*");
    }
  };
  d.addEventListener("mouseout", handleMouseOut);

  // --- Inactivity detection (15s) ---
  let idleTimer: ReturnType<typeof setTimeout>;
  let idleFired = false;
  const resetIdle = () => {
    clearTimeout(idleTimer);
    if (idleFired) return;
    idleTimer = setTimeout(() => {
      idleFired = true;
      iframe.contentWindow?.postMessage({ type: "bitpalm-inactivity" }, "*");
    }, 15_000);
  };
  const idleEvents = ["mousemove", "keydown", "scroll", "touchstart"] as const;
  idleEvents.forEach((ev) => d.addEventListener(ev, resetIdle, { passive: true }));
  resetIdle();

  // --- Public API ---
  const openWidget = () => {
    btn.style.display = "none";
    removeBubble();
    iframe.contentWindow?.postMessage({ type: "bitpalm-open" }, "*");
  };

  const closeWidget = () => {
    iframe.contentWindow?.postMessage({ type: "bitpalm-close" }, "*");
  };

  const destroy = () => {
    window.removeEventListener("message", handleMessage);
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("popstate", onNav);
    d.removeEventListener("mouseout", handleMouseOut);
    idleEvents.forEach((ev) => d.removeEventListener(ev, resetIdle));
    clearTimeout(scrollTimeout);
    clearTimeout(idleTimer);
    history.pushState = origPush;
    history.replaceState = origReplace;
    removeBubble();
    btn.remove();
    iframe.remove();
  };

  return { open: openWidget, close: closeWidget, destroy };
}
